import { Component, ViewChild, ChangeDetectorRef, OnInit, effect, signal, inject } from '@angular/core';
import { select, Store } from '@ngxs/store';
import { Observable, take } from 'rxjs';
import { MatSidenav, MatDrawerContainer, MatDrawer, MatDrawerContent } from '@angular/material/sidenav';
import { AppData } from '@shared/models/appdata.model';
import { diegoRoutes } from '@core/enums/orderflow-paths';
import { DealerClaimType } from '@core/enums/dealer-claim-type';
import { SetIsMenuOpenAction } from '@core/states/view/view.actions';
import { ViewSelectors } from '@core/states/view/view.selectors';
import { DealerStateSelectors } from '@core/states/dealer/dealer-state.selectors';
import { IconNames } from '@shared/enums/icon-names';
import { ApiSalesSummaryAction } from '@sales/states/sale/sale.actions';
import { UserSelectors } from '@core/states/user/user.selectors';
import { SaleSelectors } from '@sales/states/sale/sale.selectors';
import { ThemeService } from '@core/services/theme.service';
import { Theme } from '@support/models/theme.model';
import { Dealer } from '@core/models/dealer.model';
import { OpenDialogService } from '@core/services/open-dialog.service';
import { User } from '@core/models/user.model';
import { AuthService } from '@app/core/services/auth.service';
import { OrderService } from '@app/sales/services/order.service';
import { IdentifierType } from '@app/sales/models/sps-order-item.model';
import { AsyncPipe } from '@angular/common';
import { MenuSalesSummaryComponent } from './menu-sales-summary/menu-sales-summary.component';
import { AllowSettingsDirective } from '@app/shared/directives/allow-settings.directive';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MatDivider } from '@angular/material/divider';
import { MenuUserInfoComponent } from './menu-user-info/menu-user-info.component';
import { FeatureGateService } from '@app/shared/services/featuregate.service';

@Component({
  selector: 'app-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  imports: [
    MatDrawerContainer,
    MatDrawer,
    MenuUserInfoComponent,
    MatDivider,
    RouterLink,
    RouterLinkActive,
    MatIcon,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    AllowSettingsDirective,
    MenuSalesSummaryComponent,
    MatDrawerContent,
    AsyncPipe,
  ],
})
export class MainMenuComponent implements OnInit {
  private store = inject(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private appData = inject(AppData);
  private themeService = inject(ThemeService);
  private openDialogService = inject(OpenDialogService);
  private authService = inject(AuthService);
  private orderService = inject(OrderService);
  private featureGateService = inject(FeatureGateService);

  IconNames = IconNames;
  diegoRoutes = diegoRoutes;

  @ViewChild('sidenav') sidenav: MatSidenav;

  isMenuOpen = select(ViewSelectors.isMenuOpen);

  isGettingDealers = select(DealerStateSelectors.isGettingDealers);
  activeDealer = select(DealerStateSelectors.activeDealer);
  dealers = select(DealerStateSelectors.dealers);
  user = select(UserSelectors.getUser);
  salesSummary = select(SaleSelectors.getSalesSummary);
  hasSalesCompetitionClaim = select(DealerStateSelectors.hasSalesCompetitionClaim);
  isCompetitionAdmin = select(UserSelectors.isCompetitionAdmin);
  canMakeSales = select(DealerStateSelectors.canMakeSales);
  canUpdateImei = select(DealerStateSelectors.canUpdateImei);

  DealerClaimType = DealerClaimType;
  salesForceUrl = this.appData.salesForceUrl;

  selectedTheme$: Observable<Theme>;

  ordersWithMissingImeis = signal({
    isLoading: true,
    count: 0,
  });

  showSalesSummary = false;

  featureSalesBattle = false;

  constructor() {
    this.featureSalesBattle = this.featureGateService.isFeatureEnabled('featureSalesBattle');

    effect(() => {
      if (this.isMenuOpen()) {
        this.openMenu();
      }
    });
  }

  ngOnInit() {
    this.selectedTheme$ = this.themeService.selectedTheme;

    if (this.isMenuOpen()) {
      this.openMenu();
    }
  }

  private openMenu(): void {
    this.sidenav?.open();
    this.changeDetectorRef.detectChanges();
    this.store.dispatch(new ApiSalesSummaryAction());
    if (this.canUpdateImei()) {
      this.fetchMissingImeis();
    }
  }

  onClose() {
    this.sidenav.close();
    this.showSalesSummary = false;
    this.store.dispatch(new SetIsMenuOpenAction(false));
  }

  panelOpened() {
    this.showSalesSummary = true;
  }

  panelClosed() {
    this.showSalesSummary = false;
  }

  onCloseMethod = () => this.onClose();

  logoff() {
    this.authService.logoff();
  }

  openFeedbackDialog(data: { dealer: Dealer; user: User }) {
    this.openDialogService.openFeedbackDialog(data.dealer, data.user);
  }

  openErrorDialog(data: { user: User }) {
    this.openDialogService.openErrorDialog(data.user);
  }

  fetchMissingImeis() {
    this.ordersWithMissingImeis.set({
      isLoading: true,
      count: 0,
    });
    this.orderService
      .getMissingProductIdentifiersCount(IdentifierType.IMEI)
      .pipe(take(1))
      .subscribe({
        next: response =>
          this.ordersWithMissingImeis.set({
            isLoading: false,
            count: response,
          }),
      });
  }

  get missingImeiQueryParams() {
    return {
      showOrdersWithMissingImeis: 'yes',
      showCancelledOrders: 'no',
      showOrdersWaitingForCustomer: 'yes',
      showPendingOrders: 'yes',
      showFinishedOrders: 'yes',
      showFailedOrders: 'yes',
    };
  }
}
